import React, { FC, useMemo } from 'react';

import { Box, Divider, VStack } from '@rbilabs/universal-components';

import { IBackendCart } from '@rbi-ctg/menu';
import Picture from 'components/picture';
import { DeliveryStatus } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { useCartContext } from 'state/cart';
import { t } from 'state/intl/translations';

import DonationsRow from './donations-row';
import GrandTotal from './grand-total';
import SubTotal from './subtotal';
import TaxesAndFees from './taxes-and-fees';
import { TaxesAndFeesNextStep } from './taxes-and-fees-next-step';
import { TaxesRow } from './taxes-row';
import { ITotals } from './types';
import { useTotalTipCalculations } from './use-total-tip-calculations';

const Wrapper = VStack.withConfig({
  width: 'full',
  marginY: '$8',
});

const Separator = Divider.withConfig({
  marginX: 0,
  marginTop: '$4',
  thickness: '1',
});

const DeliveryLogoContainer = Box.withConfig({
  width: '$40',
  alignSelf: 'center',
});

export const Totals: FC<React.PropsWithChildren<ITotals>> = ({
  serverOrder,
  serviceMode,
  shouldSkipPriceOrder,
  isLoading,
}) => {
  const { calculateCartTotalWithDiscount } = useCart();

  const {
    subTotalCents = 0,
    taxCents = 0,
    rewardDiscountAmount,
    serverOrderTip,
    additionalDiscountAmount,
    deliveryFee,
    deliveryFeeDiscount,
    cart,
    delivery,
    isDeliveryOrder,
  } = useTotalTipCalculations({ serverOrder, serviceMode });

  const { featureDisclaimers } = useFeatureDisclaimers();
  const { deliveryLogo } = featureDisclaimers || {};
  const { cartTotal = 0, isCartTotalNegative = false } = calculateCartTotalWithDiscount();

  const isSignUpAfterCart = useIsSignUpAfterCartEnabled();

  const { selectedDonationCartEntry } = useCartContext();
  const donationAmount = useMemo(() => selectedDonationCartEntry?.price || 0, [
    selectedDonationCartEntry,
  ]);

  const showItemDonationRow = donationAmount > 0;

  // If "SignUp After Cart" is enabled:
  // - Use cart preview values
  // Otherwise
  // - Use server cart
  const skipOrderPricing = isSignUpAfterCart || isCartTotalNegative || shouldSkipPriceOrder;
  const cartSelected = skipOrderPricing
    ? ({
        totalCents: cartTotal,
        subTotalCents: cartTotal,
      } as IBackendCart)
    : cart;

  const deliveryLoading = serverOrder?.delivery?.status === DeliveryStatus.QUOTE_REQUESTED;
  const dropoffState = serverOrder?.delivery?.dropoff?.state;
  const otherDiscountAmount = isCartTotalNegative ? 0 : additionalDiscountAmount;
  const rewardDiscount = isCartTotalNegative ? 0 : rewardDiscountAmount;

  return (
    <Wrapper>
      <Box>
        {showItemDonationRow ? (
          <Box marginBottom={'$0'}>
            <DonationsRow
              isLoading={isLoading && !skipOrderPricing}
              totalCents={donationAmount}
              message={t('donation')}
              textProps={{ fontSize: 'md' }}
            />
          </Box>
        ) : null}

        <SubTotal
          cart={cartSelected}
          isLoading={isLoading && !skipOrderPricing}
          otherDiscountAmount={otherDiscountAmount}
          rewardDiscount={rewardDiscount}
        />

        {skipOrderPricing ? (
          <TaxesAndFeesNextStep textProps={{ fontSize: 'md' }} />
        ) : isDeliveryOrder ? (
          <TaxesAndFees
            deliveryFee={deliveryFee}
            deliveryFeeDiscount={deliveryFeeDiscount}
            dropoffState={dropoffState}
            serviceFeeCents={delivery?.serviceFeeCents ?? 0}
            smallCartFeeCents={delivery?.smallCartFeeCents ?? 0}
            geographicalFeeCents={delivery?.geographicalFeeCents ?? 0}
            taxCents={taxCents}
            serviceMode={serviceMode}
            subTotalCents={subTotalCents}
            isLoading={isLoading || deliveryLoading}
          />
        ) : (
          <TaxesRow
            mt={'$0'}
            textProps={{ fontSize: 'md' }}
            taxCents={taxCents}
            isLoading={isLoading}
          />
        )}

        <Separator />

        <GrandTotal
          cart={cartSelected}
          isLoading={isLoading && !skipOrderPricing}
          serverOrderTip={serverOrderTip}
          tipAmount={0}
        />
      </Box>
      {isDeliveryOrder && deliveryLogo?.locale ? (
        <DeliveryLogoContainer>
          <Picture image={deliveryLogo?.locale} alt={t('deliveryLogoAlt')} />
        </DeliveryLogoContainer>
      ) : null}
    </Wrapper>
  );
};

export default React.memo(Totals);
